package br.gov.go.pge.permanenciaweb.site.components

/*
 * Pure range-selection state machine for the date picker ("airport ticket" interaction).
 *
 * Kept out of the composable so the rules — two clicks, automatic ordering, the maximum span and
 * the restart when the second click would exceed it — are unit-tested without a browser.
 */

/** Inclusive range of days. [end] is null while the operator is picking the second day. */
data class DateRange(val start: CivilDate? = null, val end: CivilDate? = null) {

    val isComplete: Boolean get() = start != null && end != null

    val size: Int
        get() = when {
            start == null -> 0
            end == null -> 1
            else -> (end.toEpochDay() - start.toEpochDay() + 1).toInt()
        }

    fun days(): List<CivilDate> {
        val from = start ?: return emptyList()
        val to = end ?: from
        return generateSequence(from) { it.plusDays(1) }.takeWhile { it <= to }.toList()
    }

    fun contains(date: CivilDate): Boolean {
        val from = start ?: return false
        val to = end ?: from
        return date >= from && date <= to
    }

    fun normalized(): DateRange =
        if (start != null && end != null && end < start) DateRange(end, start) else this

    companion object {
        const val MAX_DAYS = 7
    }
}

/**
 * One click at a time:
 * 1. the first click anchors the start;
 * 2. the second click closes the range, swapping the ends when the operator picked backwards;
 * 3. if the resulting range would exceed [maxDays], the click starts a new range instead of
 *    silently truncating it (truncation would grant fewer days than the operator believes).
 */
data class RangeSelection(
    val range: DateRange = DateRange(),
    val maxDays: Int = DateRange.MAX_DAYS,
) {
    fun click(date: CivilDate): RangeSelection {
        val current = range
        return when {
            current.start == null -> RangeSelection(DateRange(date, null), maxDays)
            current.end != null -> RangeSelection(DateRange(date, null), maxDays)
            else -> {
                val candidate = DateRange(current.start, date).normalized()
                if (candidate.size > maxDays) {
                    RangeSelection(DateRange(date, null), maxDays)
                } else {
                    RangeSelection(candidate, maxDays)
                }
            }
        }
    }

    fun clear(): RangeSelection = RangeSelection(DateRange(), maxDays)
}

/**
 * Why [date] cannot close the current range, or `null` when it can.
 *
 * The picker refuses the click (instead of silently restarting the range, which reads as a bug)
 * and shows this message in red. Extracted from the composable so the rule is unit-tested.
 */
internal fun RangeSelection.clampError(date: CivilDate): String? {
    val anchor = range.start ?: return null
    if (range.end != null) return null // a third click legitimately starts a new range
    val candidate = DateRange(anchor, date).normalized()
    if (candidate.size <= maxDays) return null
    return buildString {
        append("Intervalo máximo de $maxDays dias.")
        if (date > anchor) {
            append(" O último dia possível é ")
            append(anchor.plusDays((maxDays - 1).toLong()).br)
            append(".")
        }
    }
}
